import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { ITotalAccruedInterest } from '../types';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface IGetTotalAccruedInterestResponse {
  status?: boolean;
  message?: string;
  data?: ITotalAccruedInterest;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetTotalAccruedInterestResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getTotalAccruedInterestAsync = createAsyncThunk(
  'savings/interest/totalAccrued',
  async (savingsAccountNumber: string, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.get<IGetTotalAccruedInterestResponse>(
        `/savings/interest-payments/total-accrued-interest?savingsAccountNumber=${savingsAccountNumber}`,
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const totalAccruedInterestSlice = createSlice({
  name: 'savings/interest/totalAccrued',
  initialState,
  reducers: {
    resetTotalAccruedInterest(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTotalAccruedInterestAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getTotalAccruedInterestAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetTotalAccruedInterestResponse;
      })
      .addCase(getTotalAccruedInterestAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetTotalAccruedInterest } = totalAccruedInterestSlice.actions;

export default totalAccruedInterestSlice.reducer;
